import { useCallback, useState } from "react";

import { useAtom, useSetAtom } from "../../../app-jotai";
import { ErrorMessageAtom } from "../../../errorAtom";
import { PdfFileController, WhiteboardFileAtom } from "../pdfFileState";
import { loadPDf } from "../utils/load_image";

import { uploadPdf } from "../utils/upload_pdf";

import type { PdfUploadResponse } from "../types/pdfFileResponse";
/**
 * ### docs:
 * `WhiteboardFileAtom`: save the uploaded file data for document navigator. See [useDocument](./useDocumentNavigator.ts) for document handling.
 *
 * `ErrorMessageAtom`: update the error message global state to show file upload network error. See [ErrorMessageAtom](excalidraw-app/errorAtom.ts), `App.tsx:943` for error handling.
 *
 * `isLoading`: file upload loading state.
 *
 * `uploadPdf`: upload pdf file to server. See [uploadPdf](../utils/upload_pdf.ts) for pdf upload handling.
 */
export const useUploadFile = () => {
  const [whiteboardFile, setWhiteboardFile] = useAtom(WhiteboardFileAtom);
  const pdfFiles = PdfFileController.getAllFiles(whiteboardFile);

  const setError = useSetAtom(ErrorMessageAtom);
  const [isLoading, setIsLoading] = useState(false);

  const handleUploadFile = useCallback(async () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);

    try {
      // Load the PDF file
      const pdfFile = await loadPDf();

      // Check if file already exists in storage
      const hasFile = PdfFileController.hasFile(pdfFile.name, pdfFiles);

      // Get file response either from existing files or by uploading
      const pdfFileResponse = hasFile
        ? pdfFiles.find((file) => file.filename === pdfFile.name)
        : await uploadPdf(pdfFile);

      if (!pdfFileResponse) {
        throw new Error("Failed to upload file");
      }
      setWhiteboardFile((prev) => PdfFileController.removeCurrentFile(prev));
      setTimeout(() => {
        setWhiteboardFile((prev) =>
          PdfFileController.addFile(prev, pdfFileResponse),
        );
      }, 500);
    } catch (error) {
      // Handle error and set error message
      const errorMessage =
        error instanceof Error ? error.message : "Failed to upload file";

      if (error) {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  }, [setWhiteboardFile, isLoading, pdfFiles, setError]);

  const addFile = useCallback(
    (file: PdfUploadResponse) => {
      setWhiteboardFile((prev) => PdfFileController.addFile(prev, file));
    },
    [setWhiteboardFile],
  );
  return { handleUploadFile, addFile, pdfFiles, isLoading };
};
